import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage, injectIntl } from 'react-intl';
import { differenceInDays } from 'date-fns';

import './ProductRow.scss';
import { Button, EButtonType, KitIcons } from '@repo/apg-ui';
import { createDateRange, parseToApiDate } from '../../utils/react';
import { defined } from '../../utils/variableEvaluation';
import SpinnerV2 from '../SpinnerV2';
import {
  formatDate,
  getBasketDateFromProduct,
  getDateFromProduct,
  getStartDateFromProduct,
  parsedDate,
} from '../../utils/product';

import { ReactComponent as GreenChecker } from '../../assets/icons/green_checker.svg';
import { ReactComponent as Edit } from '../../assets/icons/edit_outline.svg';
import { ReactComponent as EditPlus } from '../../assets/icons/new/add_to_basket.svg';
import { ReactComponent as Plus } from '../../assets/icons/e_i_+.svg';
import { ReactComponent as AddToBucket } from '../../assets/icons/new/basket_white.svg';
import { ReactComponent as WhiteSearch } from '../../assets/icons/white-search.svg';
import { ReactComponent as UnavailableIcon } from '../../assets/icons/icon-unavailable.svg';
import { ReactComponent as RemoveIcon } from '../../assets/icons/new/trash.svg';
import Tooltip from 'rc-tooltip';
import { ReactComponent as InfoIcon } from '../../assets/icons/info.svg';
import { ReactComponent as RightArrowIcon } from '../../assets/icons/arrow_right.svg';
import { bindActionCreators, Dispatch } from 'redux';
import * as actions from '../../actions';
import * as _actionsJourney1 from '../../actions/journey1';
import * as _actionsJourney2 from '../../actions/journey2';
import * as _actionsBasket from '../../actions/basket';
import * as _actionsProduct from '../../actions/product';
import { connect } from 'react-redux';
import { formatPrice, numberWithSpaces } from '../../utils/numbers';
import { useMedia, usePrevious, useUpdateEffect } from 'react-use';
import { useNavigate } from 'react-router';
import parse from 'html-react-parser';
import config from '../../constants/config';
import { getParameterByName } from '../../utils/toSearchParams';
import { SchemaProduct, SchemaTimeslotInformation } from '@repo/legacy-commerce-api';

interface Props {
  basketEntries?: any;
  duration?: number;
  startDate?: any;
  getProductPrice?: any;
  updateDateAndDuration?: any;
  changeProductDate?: any;
  getAvailabilityForDigitalProduct?: any;
  possibleBookingDates?: any;
  handleSelectionClick?: any;
  loading: any;
  activateSourceTab?: any;
  journey2?: boolean;
  intl: any;
  product: any;
  productNew?: any;
  isDigiflex: boolean;
  userDataEC?: any;
  isShowRemoveAction?: boolean;
  actionsJourney1: typeof _actionsJourney1;
  actionsJourney2: typeof _actionsJourney2;
  actionsBasket: typeof _actionsBasket;
  actionsProduct: typeof _actionsProduct;
  onRemoveCallback?: any;
  productsJ2ByUrn?: Record<string, SchemaProduct>;
  dispatch: any;
  isShortTimeslot: boolean;
  isHideAddingRemoval?: boolean;
  isSecondProduct?: boolean;
  isBasketRow?: boolean;
}

const ProductRow = ({
  productNew,
  product,
  startDate,
  possibleBookingDates,
  duration,
  productsJ2ByUrn,
  handleSelectionClick,
  getAvailabilityForDigitalProduct,
  intl,
  journey2,
  userDataEC,
  actionsJourney1,
  actionsJourney2,
  actionsBasket,
  onRemoveCallback,
  getProductPrice,
  isDigiflex,
  activateSourceTab,
  loading,
  basketEntries,
  isShowRemoveAction,
  isShortTimeslot,
  isHideAddingRemoval,
  isSecondProduct,
  isBasketRow,
}: Props) => {
  const date =
    startDate ||
    (possibleBookingDates && possibleBookingDates.start ? new Date(possibleBookingDates.start.date) : new Date());
  let startProduct = getStartDateFromProduct(productNew, product) || date;

  const getStartDuration = () => {
    let durationState = !!productNew && productNew.startdate ? productNew.duration : product.duration || duration;
    if (product.startdate && product.enddate && !durationState) {
      let startdatum = parsedDate(product.enddate, true);
      let enddatum = parsedDate(product.startdate, false, true);
      durationState = differenceInDays(startdatum, enddatum) + 1;
    }

    if (product && product.formatcode === 'TRA') {
      durationState = 28;
    }
    return durationState;
  };

  const startDuration = getStartDuration();
  const performanceType = product?.performancetype || 1;
  const performanceValue = product?.performancevalue || 0;

  const getPerformanceLabel = (performanceType: number) => {
    switch (performanceType) {
      case 1:
        return intl.formatMessage({
          id: 'ec.general.performanceType1',
          defaultMessage: 'Kontaktchancen pro Woche',
        });
      case 2:
        return intl.formatMessage({
          id: 'ec.general.performanceType2',
          defaultMessage: 'Fahrgastzahlen pro Woche',
        });
      default:
        return '';
    }
  };

  const [isRowOpened, setIsRowOpened] = useState<string>('');
  const [isAnimateSelection, setAnimatingSelection] = useState<boolean>(false);
  const [calendarVisible, setCalendarVisible] = useState<boolean>(false);
  const [isPriceLoading, setIsPriceLoading] = useState<boolean>(false);
  const [selectedTimeslot, setSelectedTimeslot] = useState<any>(
    productNew ? productNew.baskettimeslot : product.defaulttimeslot,
  );
  const [dateRange, setDateRange] = useState<any>([
    createDateRange(startProduct, startDuration, {
      toEndOfYear: startDuration > 28,
      fullWeeks: !(product.producttype === 'digital'),
    }),
  ]);
  const isMobile = useMedia('(max-width: 768px)');
  const lecacyMultiline = getParameterByName('legacy') === 'on' || config.hideMultipleBookedLines || isMobile;
  const [hideMultiLine, setHideMultiLine] = useState<boolean>(lecacyMultiline);
  const navigate = useNavigate();

  const prevIsRowOpened = usePrevious(isRowOpened);

  useEffect(() => {
    setCalendarVisible(product.producttype !== 'digital');
    setSelectedTimeslot(productNew ? productNew.baskettimeslot : product.defaulttimeslot);

    const range = createDateRange(startProduct, startDuration, {
      toEndOfYear: startDuration > 28,
      fullWeeks: !(product.producttype === 'digital'),
    });
    setDateRange([range]);
  }, [product, productNew]);

  useUpdateEffect(() => {
    if (isRowOpened) {
      if (prevIsRowOpened !== isRowOpened || prevIsRowOpened === '') {
        getProductPrice(product.producturn, dateRange[0]?.startDate || '');

        if (product.producttype === 'digital') {
          getAvailabilityForDigitalProduct(product.producturn, dateRange[0]?.startDate || '', 62);
        }
      }
    }
  });

  const handleCheckClick = (val, fromExpandedProduct, newProductData?) => {
    if (!handleSelectionClick) {
      return;
    }
    setAnimatingSelection(true);

    let _product = {
      ...product,
      defaulttimeslot: productsJ2ByUrn && productsJ2ByUrn[product.producturn].defaulttimeslot,
      basketkey: newProductData?.basketkey || product.basketkey,
    };
    handleSelectionClick(_product, val, newProductData)
      .then(() => {
        setTimeout(() => {
          setAnimatingSelection(false);
        }, 150);
      })
      .then(() => setIsRowOpened(''))
      .catch(() => {
        setAnimatingSelection(false);
      });
  };

  const getProductSpotLengthNumber = () => {
    let spotLength = null;

    if (productNew && productNew.basketspotlength) {
      spotLength = productNew.basketspotlength;
    } else if (isSecondProduct && product.basketspotlength) {
      spotLength = product.basketspotlength;
    } else if (product && product.spotlength) {
      spotLength = product.spotlength;
    }

    return spotLength || 10;
  };

  const getProductSpotLength = () => {
    let spotLength = getProductSpotLengthNumber();

    return !!spotLength
      ? `${spotLength} ${intl.formatMessage({
          id: 'ec.general.seconds',
          defaultMessage: 'Sekunden',
        })}`
      : '';
  };

  const getProductState = () => {
    //
    // Product states
    //
    // =0: get be sold
    // CONST SHOPSTATE_AKTIV = 0;

    // // <0: can't be sold
    // CONST SHOPSTATE_NOT_LISTED = -99;
    // CONST SHOPSTATE_NO_SALE = -1;
    // CONST SHOPSTATE_NO_PRICE_NO_SALE = -2;

    // // >0: actually to be sold, but not under certain condition
    // CONST SHOPSTATE_SALE_PREVENTED_NOT_AVALAIBLE_CERTAIN_DATE = 1;
    // CONST SHOPSTATE_SALE_PREVENTED_NOT_AVAILABLE_AT_ALL = 2;
    // CONST SHOPSTATE_SALE_PREVENTED_INDUSTRY_CODE = 3;

    // 24.05.2021
    //         0 = valid, can be booked, selected
    // -5= not selected, but can be booked
    // -10 = not valid, reason: no available to the given date (new date can be choosen)
    // -2 / -11 = not valid, new date does not change it, so the "anpassen" dialog makes no sense and can be disabled. Reason can be that the industry code does not meet the restriction. These states can be defined more detailed in the future.

    if (journey2) {
      return defined(productNew?.state) ? productNew?.state : product?.state;
    }

    return 0;
  };

  const getCoordinates = () => {
    if (product && product.latitude && product.longitude) {
      return { lat: product.latitude, lng: product.longitude };
    } else {
      const city = product ? product.city : '';

      const locationsNew = Object.values(userDataEC.locations).reduce((acc, item: any) => {
        acc[item.address] = item;

        return acc;
      }, {});

      return locationsNew && locationsNew[city] ? locationsNew[city].lngLtd : userDataEC.mapCenter;
    }
  };

  const removeFromBasketFunc = (basketEntryId: any, urn: string) => {
    setAnimatingSelection(true);
    if (isAnimateSelection) {
      return;
    }

    if (journey2) {
      const payload = {
        entries: [basketEntryId],
        basketreference: userDataEC.basketInfo.basketreference,
        basket: userDataEC.basketInfo.basket,
      };
      return actionsBasket
        .removeFromBasketByPayload(payload, urn)
        .then(() => {
          actionsBasket.getBasketContent(userDataEC.basketInfo).then(() => {
            setAnimatingSelection(false);
          });
        })
        .catch((err: any) => {
          console.error(err);
          setAnimatingSelection(false);
        });
    } else {
      return actionsBasket
        .removeFromBasket(basketEntryId, urn)
        .then(() => {
          onRemoveCallback().then(() => setAnimatingSelection(false));
        })
        .catch((err: any) => {
          console.error(err);
          setAnimatingSelection(false);
        });
    }
  };

  const getPrice = () => {
    if (product.producttype === 'digital') {
      if (productNew) {
        return productNew.pricefordates?.length > 0 ? productNew.pricefordates[0].price : productNew.standardprice;
      } else {
        return !isSecondProduct && productsJ2ByUrn[product.producturn].pricefordates?.length > 0
          ? productsJ2ByUrn[product.producturn].pricefordates[0].price
          : product.standardprice;
      }
    } else {
      return productNew?.standardprice ? productNew.standardprice : product.standardprice;
    }
  };

  const getDailyBroadcasts = (isBasketProduct: boolean) => {
    if (product.producttype === 'digital') {
      if (productNew && !isBasketProduct) {
        return productNew.basketdailytransmission;
      } else {
        return (
          (!isBasketProduct && productsJ2ByUrn[product.producturn].dailytransmission) ||
          product.baskettotaltransmission ||
          product.dailytransmission
        );
      }
    } else {
      return productNew?.basketdailytransmission ? productNew.basketdailytransmission : product.basketdailytransmission;
    }
  };

  const getTotalBroadcasts = (isBasketProduct: boolean) => {
    if (product.producttype === 'digital') {
      if (productNew && !isBasketProduct) {
        return productNew.baskettotaltransmission;
      } else {
        return (
          (!isBasketProduct && productsJ2ByUrn[product.producturn].weeklytransmission) ||
          product.baskettotaltransmission ||
          product.weeklytransmission
        );
      }
    } else {
      return productNew?.baskettotaltransmission ? productNew.baskettotaltransmission : product.weeklytransmission;
    }
  };

  const getRepetitions = () => {
    if (product.producttype === 'digital') {
      if (productNew) {
        return productNew.basketdailyrepetition || productNew.dailyrepetition;
      } else {
        return (!isSecondProduct && productsJ2ByUrn[product.producturn]?.dailyrepetition) || product.dailyrepetition;
      }
    } else {
      return productNew?.basketdailyrepetition ? productNew.basketdailyrepetition : product.dailyrepetition;
    }
  };

  const getTimeslotDescription = () => {
    for (const timeslot of timeslotsInfo) {
      if (productNew && timeslot.timeslot === productNew.baskettimeslot) {
        return (
          <div className={'mb-3 d-flex flex-row text-grey-light'}>
            <div className='font-14 font-weight-bolder'>
              {timeslot.name}: {timeslot.timespan}
            </div>
          </div>
        );
      }
    }
    return '';
  };

  const getProductTitle = (product: SchemaProduct) => {
    if (!isDigiflex) {
      return product?.format ? product.format + ' - ' + product?.name : product?.name;
    }
    return product?.name;
  };

  const productState = getProductState();
  const coordinates = getCoordinates();
  const productDate = getDateFromProduct(product);
  const _startDate =
    !!dateRange && dateRange[0] && dateRange[0].startDate && formatDate(dateRange[0].startDate, '', intl.locale);
  const endDate =
    !!dateRange && dateRange[0] && dateRange[0].endDate && formatDate(dateRange[0].endDate, '', intl.locale);
  const startDateProduct = !!productDate ? formatDate(productDate[0], '', intl.locale) : null;
  const endDateProduct = !!productDate ? formatDate(productDate[1], '', intl.locale) : null;
  const spotLengthNumber = getProductSpotLengthNumber();
  const spotLength = product.producttype === 'digital' ? getProductSpotLength() : '';
  const spotLengthTermin = isDigiflex ? '' : spotLength;

  const timeslotsInfo = product.timeslotsinformation;
  const dailyRepetitions = getRepetitions();

  const allBasketEntries = userDataEC.basketProducts.productsByUrnAll[product.producturn];
  // Wird im Fall des "verstecken" der Zusatzzeilen immer auf True gesetzt, solange allBasketEntries irgendwas enthält)
  const isBookedInPeriod =
    !!allBasketEntries &&
    !!productDate &&
    allBasketEntries.some(
      (entry) =>
        (entry.basketstartdate &&
          productDate &&
          new Date(entry.basketstartdate) <= productDate[1] &&
          new Date(entry.basketenddate) >= productDate[0] &&
          (!isDigiflex || entry.baskettimeslot === selectedTimeslot)) ||
        hideMultiLine,
    );
  const isProductInBasket = journey2 && allBasketEntries && allBasketEntries.length > 0;
  if (isProductInBasket) {
    productNew = allBasketEntries.find(
      (entry: { basketstartdate: string; basketenddate: string; baskettimeslot: any }) => {
        return (
          entry.basketstartdate &&
          productDate &&
          ((new Date(entry.basketstartdate) <= productDate[1] &&
            new Date(entry.basketenddate) >= productDate[0] &&
            (!isDigiflex || entry.baskettimeslot === selectedTimeslot)) ||
            hideMultiLine)
        );
      },
    );
  }
  const basketDate = getBasketDateFromProduct(productNew);
  const startDateBasket = !!basketDate
    ? formatDate(basketDate[0], '', intl.locale)
    : !!productDate
      ? formatDate(productDate[0], '', intl.locale)
      : '';
  const endDateBasket = !!basketDate
    ? formatDate(basketDate[1], '', intl.locale)
    : !!productDate
      ? formatDate(productDate[1], '', intl.locale)
      : '';

  // Bool auch bei "undefined"
  isSecondProduct = !!isSecondProduct;
  isBasketRow = !!isBasketRow;
  if (isBasketRow) {
    isSecondProduct = false;
  }

  if (isShowRemoveAction === undefined) {
    isShowRemoveAction = true;
  }
  isShowRemoveAction = isShowRemoveAction && (isSecondProduct || isBasketRow) && (!hideMultiLine || isBookedInPeriod);

  const basketTimeslot =
    isSecondProduct && timeslotsInfo
      ? timeslotsInfo.find((t: SchemaTimeslotInformation) => t.timeslot === product.baskettimeslot)
      : null;
  const basketTimeslotName = basketTimeslot ? basketTimeslot.name : product.baskettimeslot;
  const isUnavailable = isBasketRow && Number(productState) < 0;

  return (
    <div
      className={classNames({
        'product-row': !isSecondProduct,
        'product-row-top': isSecondProduct,
        'product-row-second': isSecondProduct,
        notAvailable: productState == -2 || productState == -11,
        unavailable: isUnavailable,
        expanded: isRowOpened === product.producturn,
        digital: product.producttype === 'digital',
        withCalendar: calendarVisible,
      })}
    >
      {!isSecondProduct && <div className='product-row-name'>{getProductTitle(product)}</div>}
      <div className='product-row-wrapper'>
        {isDigiflex && !isSecondProduct && (
          <React.Fragment>
            <div className='timeslot-tabs d-flex'>
              {timeslotsInfo &&
                !isShortTimeslot &&
                Object.keys(timeslotsInfo).map((tid, idx) => (
                  <div
                    className={classNames('slot p-4', {
                      selected: selectedTimeslot === timeslotsInfo[tid].timeslot,
                      disabled: hideMultiLine && isProductInBasket,
                    })}
                    key={`slot-${idx}`}
                    onClick={() => {
                      if (!hideMultiLine || !isProductInBasket) {
                        setSelectedTimeslot(timeslotsInfo[tid].timeslot);
                        setIsPriceLoading(true);
                        actionsJourney2
                          .getPriceForProduct({
                            producturn: product.producturn,
                            startdate: dateRange[0].startDate ? parseToApiDate(dateRange[0].startDate) : '',
                            timeslot: timeslotsInfo[tid].timeslot,
                            duration: startDuration,
                            spotlength: spotLengthNumber,
                            shareoftime: 100,
                          })
                          .then(() => {
                            getAvailabilityForDigitalProduct(product.producturn, dateRange[0]?.startDate || '', 62);
                            setIsPriceLoading(false);
                          })
                          .catch((err: any) => {
                            console.error(err);
                            setIsPriceLoading(false);
                          });
                      }
                    }}
                  >
                    <div className='font-14 font-weight-bold'>{timeslotsInfo[tid].name}</div>
                    <div className='font-12 pt-2'>{timeslotsInfo[tid].timespan}</div>
                  </div>
                ))}
              {isShortTimeslot && isDigiflex && (
                <div>
                  <div className='font-14 font-weight-bold'>{getTimeslotDescription()}</div>
                </div>
              )}
            </div>
          </React.Fragment>
        )}

        <div className={classNames('product-row-top', { notDigital: !isDigiflex })}>
          {productState == -2 && (
            <div className='notAvailableOverlay'>
              <div>
                <FormattedMessage id='ec.journey03.productNotAvailable' defaultMessage='Not available' />
                <Tooltip
                  overlayClassName={'rc-tooltip-bg-white'}
                  placement={'right'}
                  overlay={
                    <b>
                      <FormattedMessage id='ec.journey03.unavailableTooltip' defaultMessage='Not available' />
                    </b>
                  }
                >
                  <InfoIcon className='ms-3' />
                </Tooltip>
              </div>
              <div className='redirectButton'>
                <button
                  className='nonButton color-red font-500'
                  onClick={() => {
                    activateSourceTab();
                  }}
                >
                  <FormattedMessage id='ec.journey03.selectAnotherProduct' defaultMessage='Eine andere Fläche wählen' />
                  <RightArrowIcon />
                </button>
              </div>
            </div>
          )}

          <div className='product-row-column-first'>
            {product.productphotopath && !isSecondProduct ? (
              <div className='product-row-image'>
                <Button
                  className='search-box d-flex justify-content-center align-items-center'
                  buttonType={EButtonType.BasicLink}
                  onClick={() =>
                    navigate({
                      search: `?modalContent=productImage&urn=${product.producturn}`,
                    })
                  }
                >
                  <WhiteSearch />
                </Button>
                <div className='code-product'>{product?.mediainfo?.nummer}</div>
                <img alt='Product' src={product.productphotopath + '&resolution=thumbnail'} />
              </div>
            ) : (
              <div className='product-row-image no-img' />
            )}
          </div>

          <div className='product-row-column-first align-items-start justify-content-center product-icon'>
            {product.producttype === 'digital' && (
              <div className='column-title'>
                <FormattedMessage id='ec.modals.resultList.columnTitles.screens' defaultMessage='Anz. Screens' />
              </div>
            )}
            {((product.area && product.area.length > 0 && product.producttype === 'analog') ||
              product.producttype === 'traffic') && (
              <div className='column-title'>
                <FormattedMessage id='ec.modals.resultList.columnTitles.flaschen' defaultMessage='Anz. Flächen' />
              </div>
            )}
            <div className='column-details'>
              <div className='d-flex flex-row gap-2 align-items-center justify-content-center text-center w-100 product-row-icon'>
                {KitIcons.getProductIcon(product)}
                {product.mediacount && product.mediacount > 1 && <p className='mb-0'>{`${product.mediacount} `}</p>}
              </div>
            </div>
          </div>

          <div className='product-row-column-first flex-column justify-content-center product-address'>
            <div className='column-title'>
              {!isSecondProduct ? (
                <FormattedMessage id='ec.modals.resultList.columnTitles.address' defaultMessage='Adresse' />
              ) : (
                <FormattedMessage
                  id='ec.modals.resultList.columnTitles.bookedInBasket'
                  defaultMessage='bereits im Warenkorb'
                />
              )}
            </div>
            <div className='column-details'>
              <div
                className={classNames('d-flex flex-column', {
                  'align-content-between flex-wrap': journey2 && productState === '-10',
                })}
              >
                {product?.address && parse(product.address)}
                {!isDigiflex && performanceValue > 0 && (
                  <div className='w-100'>
                    <b>{getPerformanceLabel(performanceType)}</b>
                    <span>: {numberWithSpaces(performanceValue)}</span>
                  </div>
                )}
                <div className='w-100'>
                  <Button
                    className='fw-normal text-decoration-underline mt-0 text-start'
                    buttonType={EButtonType.BasicLink}
                    onClick={() => {
                      navigate({
                        search: `?modalContent=map&lat=${coordinates.lat}&lng=${coordinates.lng}&urn=${product.producturn}`,
                      });
                    }}
                  >
                    <FormattedMessage id='ec.components.buttons.seeOnMap' defaultMessage='Auf Karte ansehen' />
                  </Button>
                </div>
                {journey2 && productState === '-10' && (
                  <div className='w-100'>
                    <Button
                      className={classNames('product-row-button', {})}
                      buttonType={EButtonType.BasicLink}
                      onClick={() => activateSourceTab()}
                    >
                      <span className='icon me-3'>
                        <Plus className='small' />
                      </span>
                      <FormattedMessage
                        id='ec.components.buttons.differentProduct'
                        defaultMessage='Andere Fläche wählen'
                      />
                    </Button>
                  </div>
                )}
              </div>
              {product.notes && product.notes.length > 0 && (
                <div className='mt-3'>
                  {product.notes.map((note, idx) => (
                    <div className='py-3 px-24 font-14 bg-yellow varied-warning mb-3' key={`note-${idx}`}>
                      <b className='mr-1'>
                        {note.title ? (
                          note.title
                        ) : (
                          <FormattedMessage id='ec.modals.digiflex.note' defaultMessage='Note' />
                        )}
                      </b>
                      <br />
                      {note.note}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {isDigiflex && (
            <div className='product-row-column-first termin longer-column justify-content-center'>
              <div className='column-title'>
                {!isSecondProduct ? (
                  <FormattedMessage id='ec.modals.resultList.columnTitles.broadcasts' defaultMessage='Ausstrahlungen' />
                ) : (
                  <FormattedMessage
                    id='ec.modals.resultList.columnTitles.broadcastsInBasket'
                    defaultMessage='Details zur Ausstrahlung'
                  />
                )}
              </div>

              <div className='column-details'>
                {isSecondProduct && product.baskettimeslot && (
                  <div className='column-details-items'>
                    <b>
                      <FormattedMessage id='ec.general.digiflexBookedTimeslot' defaultMessage='Zeitfenster' />
                    </b>
                    <span>: {basketTimeslotName}</span>
                  </div>
                )}
                <div className='column-details-items'>
                  <b>
                    <FormattedMessage id='ec.general.digiflexLength' defaultMessage='Länge' />
                  </b>
                  <span>: {spotLength}</span>
                </div>
                {getDailyBroadcasts(isSecondProduct) !== 0 && !isSecondProduct && (
                  <div className='column-details-items'>
                    <b>
                      <FormattedMessage
                        id='ec.general.digiflexDailyBroadcasts'
                        defaultMessage='Tägliche Ausstrahlungen'
                      />
                    </b>
                    <span>: {numberWithSpaces(getDailyBroadcasts(isSecondProduct))}</span>
                  </div>
                )}
                {getTotalBroadcasts(isSecondProduct) !== 0 && (
                  <div className='column-details-items'>
                    <b>
                      <FormattedMessage
                        id='ec.general.digiflexTotalBroadcasts'
                        defaultMessage='Total Ausstrahlungsdauer'
                      />
                    </b>
                    <span>: {numberWithSpaces(getTotalBroadcasts(isSecondProduct))}</span>
                  </div>
                )}
                {dailyRepetitions !== 0 && dailyRepetitions !== null && (
                  <div className='column-details-items'>
                    <span>
                      <FormattedMessage
                        id='ec.general.digiflexRepetitions'
                        defaultMessage='{repsHeader}: ca. alle {repetitions} Sekunden'
                        values={{
                          repetitions: numberWithSpaces(dailyRepetitions),
                          repsHeader: (
                            <b>
                              {intl.formatMessage({
                                id: 'ec.general.repetitions',
                                defaultMessage: 'Wiederholung',
                              })}
                            </b>
                          ),
                        }}
                      />
                    </span>
                  </div>
                )}
                {performanceValue > 0 &&
                  ((selectedTimeslot == 1 && !isSecondProduct) ||
                    (isSecondProduct &&
                      product.baskettimeslot == 1 &&
                      (product.basketshareoftime == 100 || !product.basketshareoftime))) && (
                    <div className='column-details-items'>
                      <b>{getPerformanceLabel(performanceType)}</b>
                      <span>: {numberWithSpaces(performanceValue)}</span>
                    </div>
                  )}
              </div>
            </div>
          )}

          <div className='product-row-column-first termin justify-content-center'>
            <div className='column-title'>
              <FormattedMessage id='ec.modals.resultList.columnTitles.deadline' defaultMessage='Zeitraum' />
            </div>

            <div className='column-details'>
              {productState !== 0 && !isBookedInPeriod && !isSecondProduct ? (
                <>
                  {(userDataEC?.startDate || productState === 2) && (
                    <span className='d-flex align-items-center mb-3'>
                      <span className='d-flex align-self-start'>
                        <UnavailableIcon className='unavailable-checker me-3' />
                      </span>
                      {productState === 1 || productState === -10 ? (
                        <FormattedMessage
                          id='ec.general.unavailableDate'
                          defaultMessage='An diesem Datum nicht verfugbar'
                        />
                      ) : (
                        <FormattedMessage
                          id='ec.general.unavailableCompletely'
                          defaultMessage='Überhaupt nicht verfugbar'
                        />
                      )}
                    </span>
                  )}
                  {!userDataEC?.startDate && !isSecondProduct && !isProductInBasket && (
                    <span className='d-flex align-items-center mb-3'>
                      <FormattedMessage id='ec.general.noPeriodSelected' defaultMessage='Noch kein Zeitraum gewählt' />
                    </span>
                  )}
                </>
              ) : !isSecondProduct && !isBasketRow && isBookedInPeriod && !hideMultiLine ? (
                <span className='d-flex align-items-center mb-3'>
                  <FormattedMessage
                    id='ec.general.additionalDateRange'
                    defaultMessage='Zusätzlichen {property} wählen'
                    values={{
                      property: isDigiflex ? (
                        <>
                          <FormattedMessage id='ec.general.zeitraum' defaultMessage='Zeitraum' /> /{' '}
                          <FormattedMessage id='ec.general.digiflexBookedTimeslot' defaultMessage='Zeitfenster' />
                        </>
                      ) : (
                        <FormattedMessage id='ec.general.zeitraum' defaultMessage='Zeitraum' />
                      ),
                    }}
                  />
                </span>
              ) : (
                <span className='d-flex'>
                  <div className=''>{productState === 0 && <GreenChecker className='green-checker' />}</div>
                  <div className='d-flex flex-column'>
                    {isUnavailable && (
                      <span className='unavailable-label d-flex align-items-center mb-2'>
                        <UnavailableIcon className='unavailable-checker me-3' />
                        <FormattedMessage
                          id='ec.general.unavailableDate'
                          defaultMessage='An diesem Datum nicht verfugbar'
                        />
                      </span>
                    )}
                    <span>
                      <FormattedMessage
                        id='ec.general.daysAndSpotLength'
                        defaultMessage='{duration} Tage{spotLength}'
                        values={{
                          duration:
                            (isProductInBasket && hideMultiLine) || isBookedInPeriod
                              ? productNew.duration
                              : product.pricedays,
                          spotLength: spotLengthTermin,
                        }}
                      />
                    </span>
                    <span>
                      {(isProductInBasket ? startDateBasket : startDateProduct) || _startDate}{' '}
                      {`- ${(isProductInBasket ? endDateBasket : isProductInBasket) || endDateProduct || endDate}`}
                    </span>
                  </div>
                </span>
              )}
            </div>
          </div>

          <div className='product-row-column-first justify-content-center price'>
            <div className='column-title'>
              <FormattedMessage id='ec.modals.resultList.columnTitles.price' defaultMessage='Preis' />
            </div>

            <div className='column-details'>
              {isPriceLoading ? (
                <div style={{ width: 30, minHeight: 100 }} className='font-size-smaller'>
                  <SpinnerV2 relative inText />
                </div>
              ) : (
                <span className='fw-bold'>
                  CHF {product.producttype === 'digital' ? <br /> : null}
                  {formatPrice(getPrice())}
                </span>
              )}
            </div>
          </div>

          {basketEntries &&
            basketEntries.length > 0 &&
            !(journey2
              ? Boolean(isProductInBasket && productNew['state'] == 0)
              : basketEntries[product.basketkey]['state'] == 0) &&
            isShowRemoveAction &&
            !isAnimateSelection && (
              <div className='product-row-column-first product-row-column-first--checkbox colorMainImportant'>
                <FormattedMessage id='ec.basket.notAvailable' defaultMessage='nicht verfügbar' />
              </div>
            )}

          {!isHideAddingRemoval && (
            <div
              className={classNames(
                'product-row-column-first product-row-column-first--checkbox product-row-actions align-items-center justify-content-center gap-3',
                isShowRemoveAction ? 'justify-content-end' : '',
                'flex-column-reverse',
              )}
            >
              <Button
                className='product-row-button mt-0 px-4'
                buttonType={
                  isBookedInPeriod && !isSecondProduct && !hideMultiLine && !isBasketRow
                    ? EButtonType.PillMedium
                    : EButtonType.InvertedPillMedium
                }
                isDisabled={loading && loading['priceForProduct']}
                onClick={() =>
                  navigate({
                    search: `?modalContent=${
                      !userDataEC.startDate || !userDataEC.industry
                        ? 'fastbook&missingparameter=1'
                        : isDigiflex
                          ? 'digiflex'
                          : 'analog'
                    }&urn=${product.producturn}&basketkey=${isSecondProduct || isBasketRow || (hideMultiLine && isProductInBasket) ? (hideMultiLine ? productNew.basketkey : product.basketkey) : null}`,
                  })
                }
              >
                {isDigiflex ? (
                  <Tooltip
                    overlayClassName={'rc-tooltip-bg-white'}
                    placement={'left'}
                    overlayStyle={{ zIndex: 9999999999 }}
                    overlay={
                      <b>
                        <FormattedMessage id='ec.general.editProduct' defaultMessage='Budget, Datum etc. anpassen' />
                      </b>
                    }
                  >
                    <div className='d-flex justify-content-center align-items-center h-100'>
                      {isBookedInPeriod && !isSecondProduct && !hideMultiLine && !isBasketRow ? (
                        <EditPlus className='edit-svg-icon' />
                      ) : (
                        <Edit className='edit-svg-icon' />
                      )}
                    </div>
                  </Tooltip>
                ) : (
                  <div className='d-flex justify-content-center align-items-center h-100'>
                    {isBookedInPeriod && !isSecondProduct && !hideMultiLine && !isBasketRow ? (
                      <EditPlus className='edit-svg-icon' />
                    ) : (
                      <Edit className='edit-svg-icon' />
                    )}
                  </div>
                )}
              </Button>

              {!isShowRemoveAction && handleSelectionClick && !isBookedInPeriod && !isSecondProduct && (
                <Button
                  buttonType={EButtonType.PillMedium}
                  isLoading={isAnimateSelection}
                  isDisabled={productState === 2 || productState === 1 || productState === -10}
                  className={classNames('mt-0 px-4', {
                    'border-0 remove': isProductInBasket && false,
                  })}
                  id={`product-row-checkbox-${product.producturn}`}
                  onClick={() => handleCheckClick(true, false)}
                >
                  <div className='d-flex justify-content-center align-items-center h-100'>
                    <AddToBucket />
                  </div>
                </Button>
              )}

              {!isShowRemoveAction &&
                handleSelectionClick &&
                (isSecondProduct || (hideMultiLine && isBookedInPeriod)) && (
                  <Button
                    buttonType={EButtonType.InvertedPillMedium}
                    isLoading={isAnimateSelection}
                    isDisabled={productState === 2}
                    className={classNames('mt-0 px-4', {
                      'border-0 remove': true,
                    })}
                    id={`product-row-checkbox-${product.producturn}`}
                    onClick={() => handleCheckClick(false, true, productNew)}
                  >
                    <div className='remove-action'>
                      <RemoveIcon />
                    </div>
                  </Button>
                )}

              {isShowRemoveAction && !isMobile && (
                <div
                  className={'removeButton'}
                  onClick={() =>
                    removeFromBasketFunc(
                      product.basketentryid ?? basketEntries[product.basketkey]?.basketentryid,
                      product.producturn,
                    )
                  }
                >
                  <div className={'remove-action'}>
                    <RemoveIcon />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className='product-row-bottom-actions'>
          <div className='d-flex gap-3 pt-4'>
            <Button
              className='product-row-button mt-0 px-4 w-100'
              buttonType={
                isBookedInPeriod && !isBasketRow && !hideMultiLine
                  ? EButtonType.PillMedium
                  : EButtonType.InvertedPillMedium
              }
              isDisabled={loading && loading['priceForProduct']}
              onClick={() =>
                navigate({
                  search: `?modalContent=${
                    !userDataEC.startDate || !userDataEC.industry
                      ? 'fastbook&missingparameter=1'
                      : isDigiflex
                        ? 'digiflex'
                        : 'analog'
                  }&urn=${product.producturn}&basketkey=${isSecondProduct || isBasketRow || (isProductInBasket && hideMultiLine && productNew) ? (productNew?.basketkey ?? product.basketkey) : null}`,
                })
              }
            >
              {isDigiflex ? (
                <Tooltip
                  overlayClassName={'rc-tooltip-bg-white'}
                  placement={isMobile ? 'bottom' : 'left'}
                  overlayStyle={{ zIndex: 9999999999 }}
                  overlay={
                    <b>
                      <FormattedMessage id='ec.general.editProduct' defaultMessage='Budget, Datum etc. anpassen' />
                    </b>
                  }
                >
                  <div className='d-flex justify-content-center align-items-center h-100'>
                    {isBookedInPeriod && !isBasketRow && !hideMultiLine ? (
                      <EditPlus className='edit-svg-icon' />
                    ) : (
                      <Edit className='edit-svg-icon' />
                    )}
                  </div>
                </Tooltip>
              ) : (
                <div className='d-flex justify-content-center align-items-center h-100'>
                  {isBookedInPeriod && !isBasketRow && !hideMultiLine ? (
                    <EditPlus className='edit-svg-icon' />
                  ) : (
                    <Edit className='edit-svg-icon' />
                  )}
                </div>
              )}
            </Button>

            {((!isSecondProduct && !isBasketRow && !isBookedInPeriod) || isShowRemoveAction || hideMultiLine) && (
              <Button
                buttonType={isProductInBasket ? EButtonType.InvertedPillMedium : EButtonType.PillMedium}
                isLoading={isAnimateSelection}
                isDisabled={productState === 2}
                className={classNames('mt-0 px-4 w-100', {
                  'border-0 remove': isSecondProduct || isBasketRow,
                })}
                id={`product-row-checkbox-${product.producturn}`}
                onClick={() => {
                  !isBookedInPeriod && !isBasketRow
                    ? handleCheckClick(true, false)
                    : removeFromBasketFunc(
                        productNew ? productNew.basketentryid : basketEntries[product.basketkey]?.basketentryid,
                        product.producturn,
                      );
                }}
              >
                {!isSecondProduct && !isBasketRow && !isBookedInPeriod ? (
                  <div className='d-flex justify-content-center align-items-center h-100'>
                    <AddToBucket />
                  </div>
                ) : (
                  <div className={'remove-action'}>
                    <RemoveIcon />
                  </div>
                )}
              </Button>
            )}
          </div>
        </div>
        {!hideMultiLine &&
          !isSecondProduct &&
          !isBasketRow &&
          allBasketEntries &&
          allBasketEntries.map((entry) => {
            return (
              <ProductRow
                loading={loading}
                dispatch={null}
                basketEntries={[]}
                intl={intl}
                product={entry}
                journey2={journey2}
                isDigiflex={isDigiflex}
                userDataEC={userDataEC}
                actionsJourney1={actionsJourney1}
                actionsJourney2={actionsJourney2}
                actionsBasket={actionsBasket}
                actionsProduct={_actionsProduct}
                getAvailabilityForDigitalProduct={getAvailabilityForDigitalProduct}
                isShortTimeslot={isShortTimeslot}
                isSecondProduct={true}
                isShowRemoveAction={isShowRemoveAction}
                handleSelectionClick={handleSelectionClick}
                onRemoveCallback={onRemoveCallback}
              />
            );
          })}
      </div>
    </div>
  );
};

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
  return {
    actionsJourney1: bindActionCreators(_actionsJourney1 as any, dispatch),
    actionsJourney2: bindActionCreators(_actionsJourney2 as any, dispatch),
    actionsProduct: bindActionCreators(_actionsProduct as any, dispatch),
    actionsBasket: bindActionCreators(_actionsBasket as any, dispatch),
    dispatch: dispatch,
  };
}

export default connect(null, mapDispatchToProps)(injectIntl(ProductRow));
